import React, { useEffect, useMemo, useState } from 'react';
import { BellRing, Building2, Code2, Eye, Loader2, MessageSquareText, RefreshCw, RotateCcw, Save, Send, SlidersHorizontal, Webhook } from 'lucide-react';
import {
  deleteAccountHandoffNotificationRule,
  getAccountDetails,
  getHandoffNotificationRecords,
  getHandoffNotificationRules,
  previewHandoffNotification,
  testHandoffNotificationRule,
  updateAccountHandoffNotificationRule,
  updateGlobalHandoffNotificationRule,
} from '../services/api';
import {
  AccountDetail,
  HandoffNotificationRecord,
  HandoffNotificationRule,
  HandoffNotificationRuleInput,
} from '../types';

const emptyRule: HandoffNotificationRule = {
  scope: '__global__',
  enabled: false,
  count_mode: 'buyer_messages',
  threshold: 5,
  keywords: [],
  version: 1,
  webhook_configured: false,
  webhook_masked: '',
  verification_code_configured: false,
  im_provider: 'feishu',
  message_style: 'rich',
  payload_template: '',
};

const CUSTOM_WEBHOOK_TEMPLATE = `{
  "msg_type": "text",
  "content": {
    "text": "【闲鱼人工接管通知】\\n账号：{{account_name}}\\n买家：{{buyer_name}}\\n买家消息：{{buyer_message}}\\n触发原因：{{reason}}\\n买家有效发言：{{buyer_message_count}}\\n发送时间：{{occurred_at}}"
  }
}`;

const providerOptions = [
  { id: 'feishu', label: '飞书', description: '卡片通知，校验码用于签名', icon: MessageSquareText },
  { id: 'dingtalk', label: '钉钉', description: 'Markdown 通知，校验码用于签名', icon: Building2 },
  { id: 'wecom', label: '企业微信', description: 'Markdown 通知，地址中的 key 已校验', icon: Building2 },
  { id: 'custom', label: '自定义', description: '按 JSON 格式发送给任意系统', icon: Webhook },
] as const;

const providerHelp: Record<HandoffNotificationRuleInput['im_provider'] & string, { codeLabel: string; codeHint: string; urlHint: string }> = {
  feishu: { codeLabel: '飞书签名校验码', codeHint: '在飞书机器人安全设置中启用签名后填写；系统会自动生成签名。', urlHint: '粘贴飞书群机器人 Webhook 地址' },
  dingtalk: { codeLabel: '钉钉签名密钥', codeHint: '在钉钉机器人安全设置中选择“加签”后填写密钥；系统会自动生成签名。', urlHint: '粘贴钉钉机器人 Webhook 地址' },
  wecom: { codeLabel: '企业微信校验码（可选）', codeHint: '企业微信机器人由 Webhook 地址中的 key 校验，无需额外填写校验码。', urlHint: '粘贴企业微信群机器人 Webhook 地址' },
  custom: { codeLabel: 'Webhook 校验码', codeHint: '发送时会写入 JSON 的 verification_code 字段，由接收系统自行校验。', urlHint: '粘贴接收系统提供的 Webhook 地址' },
};

const HandoffNotifications: React.FC = () => {
  const [accounts, setAccounts] = useState<AccountDetail[]>([]);
  const [globalRule, setGlobalRule] = useState<HandoffNotificationRule>(emptyRule);
  const [accountRules, setAccountRules] = useState<Record<string, HandoffNotificationRule>>({});
  const [records, setRecords] = useState<HandoffNotificationRecord[]>([]);
  const [scope, setScope] = useState('__global__');
  const [rule, setRule] = useState<HandoffNotificationRuleInput>({
    enabled: false, count_mode: 'buyer_messages', threshold: 5, keywords: [], webhook_url: '', verification_code: '', im_provider: 'feishu', message_style: 'rich', payload_template: '',
  });
  const [keywordDraft, setKeywordDraft] = useState('');
  const [webhookStatus, setWebhookStatus] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [message, setMessage] = useState('');
  const [payloadPreview, setPayloadPreview] = useState('');

  const selectedStoredRule = useMemo(() => {
    if (scope === '__global__') return globalRule;
    return accountRules[scope] || globalRule;
  }, [scope, globalRule, accountRules]);

  const hasAccountOverride = scope !== '__global__' && Boolean(accountRules[scope]);
  const selectedProvider = rule.im_provider || 'feishu';
  const selectedProviderHelp = providerHelp[selectedProvider];
  const needsVerificationCode = selectedProvider !== 'wecom';

  const load = async () => {
    setLoading(true);
    try {
      const [rules, loadedAccounts, loadedRecords] = await Promise.all([
        getHandoffNotificationRules(), getAccountDetails(), getHandoffNotificationRecords(),
      ]);
      setGlobalRule(rules.global_rule || emptyRule);
      setAccountRules(rules.account_rules || {});
      setAccounts(loadedAccounts || []);
      setRecords(loadedRecords || []);
      setMessage('');
    } catch (error) {
      setMessage(`加载失败：${error instanceof Error ? error.message : '请稍后重试'}`);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  useEffect(() => {
    const stored = scope === '__global__' ? globalRule : (accountRules[scope] || globalRule);
    setRule({
      enabled: stored.enabled,
      count_mode: stored.count_mode,
      threshold: stored.threshold,
      keywords: stored.keywords,
      webhook_url: '',
      verification_code: '',
      im_provider: stored.im_provider || 'feishu',
      message_style: stored.message_style || 'rich',
      payload_template: stored.payload_template || '',
    });
    setKeywordDraft(stored.keywords.join('\n'));
    setWebhookStatus(stored.webhook_masked || '尚未配置');
  }, [scope, globalRule, accountRules]);

  const parseKeywords = (value: string) =>
    Array.from(new Set(value.split(/[\n,，]/).map((item) => item.trim()).filter(Boolean)));

  const save = async () => {
    setSaving(true);
    setMessage('');
    try {
      const ruleToSave = { ...rule, keywords: parseKeywords(keywordDraft) };
      if (scope === '__global__') {
        await updateGlobalHandoffNotificationRule(ruleToSave);
      } else {
        await updateAccountHandoffNotificationRule(scope, ruleToSave);
      }
      await load();
      setMessage('规则已保存');
    } catch (error) {
      setMessage(`保存失败：${error instanceof Error ? error.message : '请检查配置'}`);
    } finally {
      setSaving(false);
    }
  };

  const testWebhook = async () => {
    setTesting(true);
    setMessage('');
    try {
      const result = await testHandoffNotificationRule(scope);
      setMessage(`${result.message || '测试通知已发送'}，发送时间：${result.sent_at || '刚刚'}。请在接收群核对这条测试消息。`);
    } catch (error) {
      setMessage(`测试失败：${error instanceof Error ? error.message : '请检查 Webhook 地址'}`);
    } finally {
      setTesting(false);
    }
  };

  const previewPayload = async () => {
    setMessage('');
    try {
      const preview = await previewHandoffNotification({ ...rule, keywords: parseKeywords(keywordDraft) });
      setPayloadPreview(JSON.stringify(preview.payload, null, 2));
    } catch (error) {
      setPayloadPreview('');
      setMessage(`格式预览失败：${error instanceof Error ? error.message : '请检查 JSON 格式'}`);
    }
  };

  const resetAccountOverride = async () => {
    if (scope === '__global__') return;
    setSaving(true);
    setMessage('');
    try {
      await deleteAccountHandoffNotificationRule(scope);
      await load();
      setMessage('该账号已恢复继承全局规则');
    } catch (error) {
      setMessage(`恢复失败：${error instanceof Error ? error.message : '请稍后重试'}`);
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <div className="p-8 text-center text-gray-400">加载通知规则中...</div>;

  return (
    <div className="max-w-6xl mx-auto space-y-8 animate-fade-in pb-24">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex items-center gap-4">
          <div className="w-12 h-12 bg-amber-100 rounded-xl flex items-center justify-center">
            <BellRing className="w-6 h-6 text-amber-700" />
          </div>
          <div>
            <h2 className="text-3xl font-extrabold text-gray-900">通知与人工接管</h2>
            <p className="text-gray-500 mt-1 text-sm font-medium">达到会话阈值或命中关键词时，通知人工处理</p>
          </div>
        </div>
        <button onClick={load} className="self-start sm:self-auto px-4 py-2 bg-gray-100 hover:bg-gray-200 rounded-lg font-bold text-gray-700 flex items-center gap-2 transition-colors">
          <RefreshCw className="w-4 h-4" /> 刷新
        </button>
      </div>

      <section className="bg-white border border-gray-100 shadow-sm rounded-lg p-6 space-y-6">
        <div className="grid grid-cols-1 lg:grid-cols-[260px_1fr] gap-8">
          <div className="space-y-3">
            <label className="block text-sm font-bold text-gray-800">规则作用范围</label>
            <select value={scope} onChange={(event) => setScope(event.target.value)} className="w-full ios-input px-3 py-3 rounded-lg">
              <option value="__global__">全局默认规则</option>
              {accounts.map((account) => (
                <option key={account.id} value={account.id}>
                  {account.nickname || account.remark || account.id}
                  {accountRules[account.id] ? '（独立规则）' : '（继承全局）'}
                </option>
              ))}
            </select>
            {scope !== '__global__' && (
              <div className="text-xs text-gray-500 leading-5">
                {hasAccountOverride ? '该账号正在使用独立规则。' : '该账号当前继承全局默认规则；保存后将创建独立规则。'}
              </div>
            )}
          </div>

          <div className="space-y-6">
            <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
              <div>
                <div className="font-bold text-gray-900">启用人工接管通知</div>
                <div className="text-xs text-gray-500 mt-1">同一会话满足规则后只通知一次</div>
              </div>
              <button
                onClick={() => setRule({ ...rule, enabled: !rule.enabled })}
                className={`xsb-switch ${rule.enabled ? 'xsb-switch-on bg-[#FFE815]' : 'bg-gray-300'}`}
                data-state={rule.enabled ? 'on' : 'off'}
                aria-label="切换人工接管通知"
              >
                <span className="xsb-switch-thumb" />
              </button>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
              <div className="space-y-2">
                <label className="block text-sm font-bold text-gray-800">计数方式</label>
                <select value={rule.count_mode} onChange={(event) => setRule({ ...rule, count_mode: event.target.value as HandoffNotificationRuleInput['count_mode'] })} className="w-full ios-input px-3 py-3 rounded-lg">
                  <option value="buyer_messages">买家有效发言数</option>
                  <option value="round_trips">双方来回轮次</option>
                </select>
              </div>
              <div className="space-y-2">
                <label className="block text-sm font-bold text-gray-800">触发阈值</label>
                <input type="number" min="1" max="99" value={rule.threshold} onChange={(event) => setRule({ ...rule, threshold: Math.min(99, Math.max(1, Number(event.target.value) || 1)) })} className="w-full ios-input px-3 py-3 rounded-lg" />
              </div>
            </div>

            <div className="space-y-2">
              <label className="block text-sm font-bold text-gray-800">关键词触发</label>
              <textarea value={keywordDraft} onChange={(event) => setKeywordDraft(event.target.value)} className="w-full ios-input px-3 py-3 rounded-lg min-h-[88px] resize-y" placeholder="每行一个关键词，也可使用逗号分隔，例如：报价，急，办不了" />
            </div>

            <div className="space-y-3">
              <div>
                <label className="block text-sm font-bold text-gray-800">接收渠道</label>
                <p className="text-xs text-gray-500 mt-1">选择后会按对应平台的机器人协议、签名方式和消息格式发送。</p>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {providerOptions.map((option) => {
                  const Icon = option.icon;
                  const selected = selectedProvider === option.id;
                  return (
                    <button key={option.id} type="button" onClick={() => setRule({ ...rule, im_provider: option.id })} className={`text-left rounded-lg border p-4 transition-colors ${selected ? 'border-amber-400 bg-amber-50 ring-1 ring-amber-200' : 'border-gray-200 hover:border-gray-300 bg-white'}`}>
                      <div className="flex items-center gap-3">
                        <span className={`w-9 h-9 rounded-lg flex items-center justify-center ${selected ? 'bg-amber-200 text-amber-900' : 'bg-gray-100 text-gray-600'}`}><Icon className="w-4 h-4" /></span>
                        <span>
                          <span className="block text-sm font-bold text-gray-900">{option.label}</span>
                          <span className="block text-xs leading-5 text-gray-500 mt-0.5">{option.description}</span>
                        </span>
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>

            <div className="space-y-3">
              <div className="flex items-center gap-2"><SlidersHorizontal className="w-4 h-4 text-gray-600" /><label className="block text-sm font-bold text-gray-800">通知样式</label></div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <button type="button" onClick={() => setRule({ ...rule, message_style: 'rich' })} className={`text-left rounded-lg border p-4 transition-colors ${rule.message_style !== 'compact' ? 'border-amber-400 bg-amber-50 ring-1 ring-amber-200' : 'border-gray-200 hover:border-gray-300'}`}>
                  <span className="block text-sm font-bold text-gray-900">信息卡片</span><span className="block text-xs text-gray-500 leading-5 mt-1">重点信息分行展示，适合处理会话。</span>
                </button>
                <button type="button" onClick={() => setRule({ ...rule, message_style: 'compact' })} className={`text-left rounded-lg border p-4 transition-colors ${rule.message_style === 'compact' ? 'border-amber-400 bg-amber-50 ring-1 ring-amber-200' : 'border-gray-200 hover:border-gray-300'}`}>
                  <span className="block text-sm font-bold text-gray-900">精简文字</span><span className="block text-xs text-gray-500 leading-5 mt-1">单条纯文字消息，适合通知较多的群。</span>
                </button>
              </div>
            </div>

            <div className="space-y-2">
              <label className="block text-sm font-bold text-gray-800">Webhook 地址</label>
              <div className="flex items-center gap-2 text-xs text-gray-500"><Webhook className="w-4 h-4" /> 当前状态：{webhookStatus}</div>
              <input type="password" value={rule.webhook_url || ''} onChange={(event) => setRule({ ...rule, webhook_url: event.target.value })} className="w-full ios-input px-3 py-3 rounded-lg" placeholder={`${selectedProviderHelp.urlHint}；留空则保留当前已保存的地址`} autoComplete="new-password" />
            </div>

            <div className="space-y-2">
              <label className="block text-sm font-bold text-gray-800">{selectedProviderHelp.codeLabel}</label>
              <div className="text-xs text-gray-500">当前状态：{selectedStoredRule.verification_code_configured ? '已配置' : '尚未配置'}；{selectedProviderHelp.codeHint}</div>
              <input type="password" value={rule.verification_code || ''} onChange={(event) => setRule({ ...rule, verification_code: event.target.value })} className="w-full ios-input px-3 py-3 rounded-lg" placeholder={needsVerificationCode ? '留空则保留当前已保存的校验码；首次启用时必须填写' : '通常无需填写；留空不会清除已保存内容'} autoComplete="new-password" />
            </div>

            {selectedProvider === 'custom' && <div className="space-y-2">
              <label className="block text-sm font-bold text-gray-800 flex items-center gap-2"><Code2 className="w-4 h-4" /> 通知格式</label>
              <textarea value={rule.payload_template || ''} onChange={(event) => setRule({ ...rule, payload_template: event.target.value })} className="w-full ios-input px-3 py-3 rounded-lg min-h-[220px] resize-y font-mono text-xs leading-5" spellCheck={false} />
              <p className="text-xs text-gray-500 leading-5">可使用：event、message、buyer_name、buyer_message、buyer_message_count、round_trip_count、reason、occurred_at。系统会在 JSON 中附加 verification_code。</p>
              <div className="flex flex-wrap gap-3">
                <button onClick={() => setRule({ ...rule, payload_template: CUSTOM_WEBHOOK_TEMPLATE })} type="button" className="px-4 py-2 rounded-lg bg-blue-50 hover:bg-blue-100 text-blue-800 font-bold text-sm inline-flex items-center gap-2">恢复默认 JSON</button>
                <button onClick={previewPayload} type="button" className="px-4 py-2 rounded-lg bg-gray-100 hover:bg-gray-200 text-gray-800 font-bold text-sm inline-flex items-center gap-2"><Eye className="w-4 h-4" /> 预览通知格式</button>
              </div>
              {payloadPreview && <pre className="max-h-72 overflow-auto bg-gray-950 text-gray-100 rounded-lg p-4 text-xs leading-5 whitespace-pre-wrap break-words">{payloadPreview}</pre>}
            </div>}

            {selectedProvider !== 'custom' && <div className="rounded-lg border border-gray-200 bg-gray-50 px-4 py-3 flex flex-wrap items-center justify-between gap-3">
              <div className="text-sm text-gray-600">当前会按 {providerOptions.find((option) => option.id === selectedProvider)?.label} 的原生机器人格式发送。</div>
              <button onClick={previewPayload} type="button" className="px-3 py-2 rounded-lg bg-white border border-gray-200 hover:bg-gray-100 text-gray-800 font-bold text-sm inline-flex items-center gap-2"><Eye className="w-4 h-4" /> 预览请求内容</button>
            </div>}
            {payloadPreview && selectedProvider !== 'custom' && <pre className="max-h-72 overflow-auto bg-gray-950 text-gray-100 rounded-lg p-4 text-xs leading-5 whitespace-pre-wrap break-words">{payloadPreview}</pre>}

            {message && <div className="text-sm font-medium text-gray-700 bg-gray-50 border border-gray-100 px-4 py-3 rounded-lg">{message}</div>}

            <div className="flex flex-wrap gap-3 pt-1">
              <button onClick={save} disabled={saving} className="ios-btn-primary px-5 py-3 rounded-lg font-bold flex items-center gap-2 disabled:opacity-60">
                {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} 保存规则
              </button>
              <button onClick={testWebhook} disabled={testing || saving || !selectedStoredRule.webhook_configured || (needsVerificationCode && !selectedStoredRule.verification_code_configured)} className="px-5 py-3 rounded-lg bg-gray-100 hover:bg-gray-200 text-gray-800 font-bold flex items-center gap-2 disabled:opacity-50">
                {testing ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />} 测试通知
              </button>
              {hasAccountOverride && (
                <button onClick={resetAccountOverride} disabled={saving} className="px-4 py-3 rounded-lg text-gray-600 hover:bg-gray-50 font-bold flex items-center gap-2 disabled:opacity-50">
                  <RotateCcw className="w-4 h-4" /> 恢复继承
                </button>
              )}
            </div>
          </div>
        </div>
      </section>

      <section className="space-y-3">
        <h3 className="text-lg font-extrabold text-gray-800">最近通知记录</h3>
        <div className="overflow-x-auto bg-white border border-gray-100 rounded-lg">
          <table className="min-w-full text-sm">
            <thead className="bg-gray-50 text-gray-500 text-left">
              <tr><th className="px-4 py-3 font-bold">时间</th><th className="px-4 py-3 font-bold">账号</th><th className="px-4 py-3 font-bold">触发原因</th><th className="px-4 py-3 font-bold">状态</th><th className="px-4 py-3 font-bold">失败原因</th></tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {records.length === 0 ? <tr><td colSpan={5} className="px-4 py-8 text-center text-gray-400">暂无通知记录</td></tr> : records.map((record, index) => (
                <tr key={`${record.cookie_id}-${record.chat_id}-${record.rule_version}-${index}`}>
                  <td className="px-4 py-3 text-gray-600 whitespace-nowrap">{record.created_at}</td>
                  <td className="px-4 py-3 text-gray-800">{record.account_name || record.cookie_id}</td>
                  <td className="px-4 py-3 text-gray-700">{record.reason}</td>
                  <td className="px-4 py-3"><span className={record.status === 'sent' ? 'text-emerald-700' : record.status === 'failed' ? 'text-red-600' : 'text-amber-700'}>{record.status === 'sent' ? '已发送' : record.status === 'failed' ? '失败' : '发送中'}</span></td>
                  <td className="px-4 py-3 text-gray-500 max-w-xs truncate">{record.error_message || '-'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>
    </div>
  );
};

export default HandoffNotifications;
